<template>
  <div class="booking-lookup">
    <p class="booking-lookup__intro">{{ $t('Enter your booking reference and last name to find your booking.') }}</p>

    <form @submit.prevent="handleSubmit">
      <div class="form-field">
        <label class="form-label">{{ $t('Booking reference') }}</label>
        <input
          v-model="form.orderRef"
          type="text"
          class="form-input"
          :placeholder="$t('Order ID or confirmation number')"
          required
        />
      </div>

      <div class="form-field">
        <label class="form-label">{{ $t('Last name') }}</label>
        <input
          v-model="form.lastName"
          type="text"
          class="form-input"
          :placeholder="$t('Last name on booking')"
          required
        />
      </div>

      <div v-if="error" class="alert alert--error">
        {{ error }}
      </div>

      <div v-if="booking" class="alert alert--success">
        <p class="alert__title">{{ $t('Booking found!') }}</p>
        <NuxtLinkLocale
          :to="`/orders/${booking.uid}:${booking.secret}`"
          class="btn btn-primary btn-size-l"
        >
          {{ $t('Manage Booking') }}
        </NuxtLinkLocale>
      </div>

      <button
        v-if="!booking"
        type="submit"
        :disabled="loading"
        class="btn btn-primary btn-size-l booking-lookup__submit"
      >
        {{ loading ? $t('Searching...') : $t('Find Booking') }}
      </button>
    </form>
  </div>
</template>

<script setup lang="ts">
const { $executeRecaptcha, $i18n } = useNuxtApp()

const form = reactive({
  orderRef: '',
  lastName: '',
})
const loading = ref(false)
const error = ref('')
const booking = ref<{ uid: string; secret: string } | null>(null)

async function handleSubmit() {
  loading.value = true
  error.value = ''
  booking.value = null

  try {
    const recaptchaToken = await $executeRecaptcha('support_lookup')
    const result = await $fetch<{ uid: string; secret: string }>('/api/order/v3/orders/support-lookup', {
      method: 'POST',
      body: {
        order_ref: form.orderRef,
        last_name: form.lastName,
        recaptcha_token: recaptchaToken,
      },
    })
    booking.value = result
  } catch (e: any) {
    if (e?.response?.status === 404 || e?.statusCode === 404) {
      error.value = $i18n.t('No booking found. Please check your reference and last name.')
    } else if (e?.response?.status === 403 || e?.statusCode === 403) {
      error.value = $i18n.t('Verification failed. Please try again.')
    } else {
      error.value = $i18n.t('Something went wrong. Please try again later.')
    }
  } finally {
    loading.value = false
  }
}
</script>

<style lang="scss" scoped>
.booking-lookup {
  &__intro {
    font-size: 14px;
    color: var(--dark-grey);
    margin: 0 0 20px;
  }

  &__submit {
    width: 100%;
  }
}

.form-field {
  margin-bottom: 16px;
}

.form-label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: var(--black);
  margin-bottom: 6px;
}

.form-input {
  width: 100%;
  padding: 10px 14px;
  font-size: 14px;
  border: 1px solid var(--grey-border);
  border-radius: 6px;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
  box-sizing: border-box;

  &:focus {
    border-color: var(--blue);
    box-shadow: 0 0 0 2px var(--blue-highlight, rgba(25, 133, 249, 0.15));
  }

  &::placeholder {
    color: var(--dark-grey-secondary, #838383);
  }
}

.alert {
  padding: 14px 16px;
  border-radius: 6px;
  margin-bottom: 16px;
  font-size: 14px;

  &--error {
    background: #FFF3F3;
    border: 1px solid #E57373;
    color: #C62828;
  }

  &--success {
    background: #E8F5E9;
    border: 1px solid #81C784;

    .btn {
      margin-top: 10px;
    }
  }

  &__title {
    font-weight: 600;
    color: #2E7D32;
    margin: 0 0 4px;
  }
}
</style>
